import React, { useEffect, useRef } from 'react';
import { placesSearchCountryRestriction } from '../../config/geoLocale';
import { useGoogleMaps, useGoogleMapsRequest } from '../../contexts/GoogleMapsLoaderContext';

type Props = {
  autocompleteKey: string;
  geoCountry: string;
  inputId: string;
  value: string;
  placeholder: string;
  inputClassName?: string;
  /** Une fois true, reste true : charge le SDK Maps sans remonter l’input. */
  mapsRequested: boolean;
  onLoad: (autocomplete: google.maps.places.Autocomplete) => void;
  onPlaceChanged: () => void;
  onValueChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onClearSelectedPlace: () => void;
  onRequestMaps?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

/** Champ « Où » : un seul input stable ; Places branché sur la ref quand Maps est prêt. */
const SearchBarOuField: React.FC<Props> = ({
  autocompleteKey,
  geoCountry,
  inputId,
  value,
  placeholder,
  inputClassName = 'search-input',
  mapsRequested,
  onLoad,
  onPlaceChanged,
  onValueChange,
  onFocus,
  onBlur,
  onClearSelectedPlace,
  onRequestMaps,
  onKeyDown,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onPlaceChangedRef = useRef(onPlaceChanged);
  const onLoadRef = useRef(onLoad);
  onPlaceChangedRef.current = onPlaceChanged;
  onLoadRef.current = onLoad;

  useGoogleMapsRequest(mapsRequested);
  const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps();

  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapsRequested) return undefined;
    const input = inputRef.current;
    const places = window.google?.maps?.places;
    if (!input || !places) return undefined;

    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    const ac = new places.Autocomplete(input, {
      componentRestrictions: {
        country: placesSearchCountryRestriction(geoCountry),
      },
    });
    autocompleteRef.current = ac;
    onLoadRef.current(ac);

    const listener = ac.addListener('place_changed', () => {
      onPlaceChangedRef.current();
    });

    return () => {
      const mapsEvent = window.google?.maps?.event;
      if (mapsEvent) {
        mapsEvent.removeListener(listener);
        if (autocompleteRef.current) {
          mapsEvent.clearInstanceListeners(autocompleteRef.current);
        }
      }
      autocompleteRef.current = null;
    };
  }, [isGoogleMapsLoaded, mapsRequested, geoCountry, autocompleteKey]);

  return (
    <input
      ref={inputRef}
      id={inputId}
      className={inputClassName}
      type='text'
      placeholder={placeholder}
      autoComplete='off'
      value={value}
      onChange={e => {
        onValueChange(e.target.value);
        onClearSelectedPlace();
      }}
      onFocus={() => {
        onRequestMaps?.();
        onFocus();
      }}
      onPointerDown={() => onRequestMaps?.()}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
};

export default SearchBarOuField;
